import { Check, Loader2, AlertCircle, X } from 'lucide-react';
import { toLocaleTimeStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface FloatingSaveBarProps {
  hasChanges: boolean;
  saving: boolean;
  saved: boolean;
  lastSaved?: Date;
  onSave: () => void;
  onDiscard: () => void;
}

export function FloatingSaveBar({
  hasChanges,
  saving,
  saved,
  lastSaved,
  onSave,
  onDiscard,
}: FloatingSaveBarProps) {
  const tz = useTimezone();
  if (!hasChanges && !saving && !saved) return null;

  return (
    <div className="fixed bottom-0 left-0 lg:left-[280px] right-0 z-40 animate-in slide-in-from-bottom duration-300">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-6">
        <div className="bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-2xl shadow-2xl p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {hasChanges && !saving && !saved && (
                <>
                  <div className="w-2 h-2 bg-yellow-300 rounded-full animate-pulse" />
                  <span className="text-white font-medium">You have unsaved changes</span>
                </>
              )}
              {saving && (
                <>
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                  <span className="text-white font-medium">Saving changes...</span>
                </>
              )}
              {saved && (
                <>
                  <Check className="w-5 h-5 text-white" />
                  <span className="text-white font-medium">Changes saved successfully</span>
                </>
              )}
              {lastSaved && (
                <span className="text-white/70 text-sm ml-2">
                  Last saved {toLocaleTimeStringInTz(lastSaved, tz)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {hasChanges && (
                <>
                  <button
                    onClick={onDiscard}
                    disabled={saving}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2 font-medium text-sm"
                  >
                    <X className="w-4 h-4" />
                    Discard
                  </button>
                  <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-6 py-2 bg-white text-[#1875FF] rounded-lg hover:bg-white/90 transition-all disabled:opacity-50 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              )}
              <span className="text-white/70 text-xs hidden sm:block">
                Cmd/Ctrl + S
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
